// framework/client/core/TranslationManager.js

export class TranslationManager {
    constructor(appClient) {
        this.appClient = appClient;
        this.translations = {};
        this.currentLanguage = null;
        this.defaultLanguage = 'en';  // Default language
        this.availableLanguages = []; // Populated from config
        this.translationFiles = []; // Populated from config
        this.localStorageKey = "userLanguage";
        this.untranslatedKeys = new Set(); // Cache for untranslated keys
        this.translationSelectors = null; // Initialize
        this.log = new this.appClient.Logger(this.constructor.name);
        this.isInitialized = false;


    }
    async initialize() {
        let clientConfig = this.appClient.sessionTokenHandler
            ? await this.appClient.sessionTokenHandler.getClientConfig()
            : this.appClient.config;
        if (!clientConfig) {
            clientConfig = {};
        }

        this.availableLanguages = clientConfig.translations?.supportedLanguages || [{ code: "en", name: "English", dir: "ltr" }];
        this.appClient.events.fireLocal('languagesInitialized'); // Fire event after languages are loaded
        this.translationFiles = clientConfig.translations?.translationFiles ||
            [   //'/assets/translations/framework.js',
                //'/assets/translations/app.js',
                '/public-files/app-translations.js'
            ]; // Default files
        this.forceTranslate = clientConfig.translations?.forceTranslate || false;


        this.loadUserLanguage();  // Load and set currentLanguage *before* loading translations
        await this.loadTranslations(this.currentLanguage);
        this.initializeSelectors(clientConfig); // Initialize selectors
        this.appClient.events.local.on('displayLanguageChanged', async ({ language }) => {
            await this.loadTranslations(language); //when language changes
            this.applyTranslations();
        });
        // Listen for ALL_MODULES_INITIALIZED
        this.appClient.events.local.on('ALL_MODULES_INITIALIZED', async () => {
            if (!this.isInitialized) { //for safety
                await this.loadTranslations(this.currentLanguage); // Initial load
                this.applyTranslations();
                this.isInitialized = true;
            }

        });

        document.addEventListener("show.bs.modal", (event) => {
            this.log.info("Modal shown, applying translation...");
            this.applyTranslations(event.target);
        });

    }


    initializeSelectors(clientConfig) {
        const defaultTranslationSelectors = {
            attributeSelectors: [
                { selector: '[data-i18n]', keyAttribute: 'data-i18n' },
                { selector: 'button[title]', keyAttribute: 'title' },
                { selector: 'span[title]', keyAttribute: 'title' },
                { selector: 'div[title]', keyAttribute: 'title' },
                { selector: 'input[placeholder]', keyAttribute: 'placeholder' },
                { selector: 'textarea[placeholder]', keyAttribute: 'placeholder' },
            ],
            elementSelectors: [
                { selector: 'label', keyFromContent: true },
                { selector: 'th', keyFromContent: true },
                { selector: 'h5', keyFromContent: true },
                { selector: 'button > span', keyFromContent: true },
                { selector: 'button:not(:has(span))', keyFromContent: true },
                { selector: 'a:not(:has(span))', keyFromContent: true },
                { selector: 'a > span', keyFromContent: true },
                { selector: 'button:not([data-dynamic]) > span', keyFromContent: true },
                { selector: 'legend', keyFromContent: true },
                { selector: 'option', keyFromContent: true },
                { selector: 'div.field-label', keyFromContent: true },
                { selector: 'small.text-muted', keyFromContent: true },

            ],
            customSelectors: [],
        };

        // Use client config, or defaults if not provided
        this.translationSelectors = clientConfig.translations?.selectors || defaultTranslationSelectors;
    }

    getAvailableLanguages() {
        return this.availableLanguages;
    }

    getCurrentLanguage() {
        return this.currentLanguage;
    }

    async setLanguage(languageCode) {
        const selectedLanguage = this.availableLanguages.find(lang => lang.code === languageCode);
        if (!selectedLanguage) {
            console.error(`Language ${languageCode} not found.`);
            return;
        }

        this.currentLanguage = languageCode;
        localStorage.setItem(this.localStorageKey, languageCode); // Save to localStorage
        document.documentElement.setAttribute('lang', languageCode);
        document.documentElement.setAttribute('dir', selectedLanguage.dir);
        this.appClient.events.local.emit('displayLanguageChanged', { language: languageCode });

    }

    async loadTranslations(selectedLanguage, applyTranslations = true) {
        this.translations = {}; // Reset translations
        for (const file of this.translationFiles) {
            try {
                const filePath = `${file}`;
                let module;
                if (filePath.endsWith('.js')) {
                    module = await import(filePath); // Dynamic import for .js
                }
                else {
                    const response = await fetch(filePath);
                    if (!response.ok) {
                        throw new Error(`HTTP error ${response.status} fetching ${filePath}`);
                    }
                    module = await response.json();
                }
                this.mergeTranslations(module.default || module); // Handle default export or direct object
            } catch (error) {
                this.log.warn(`Error loading translation file ${file}:`, error);
            }
        }
    }


    mergeTranslations(newTranslations) {
        for (const key in newTranslations) {
            if (Object.hasOwn(newTranslations, key)) {
                if (!this.translations[key]) {
                    this.translations[key] = {};
                }
                // Merge translations for each language
                for (const lang in newTranslations[key]) {
                    if (Object.hasOwn(newTranslations[key], lang)) {
                        this.translations[key][lang] = newTranslations[key][lang];
                    }
                }
            }
        }
    }

    applyTranslations(rootElement = document) {
        const root = rootElement;
        const elements = [];

        // 1. Gather elements by attribute selectors.
        this.translationSelectors.attributeSelectors.forEach(({ selector }) => {
            elements.push(...root.querySelectorAll(selector));
        });

        // 2. Gather elements by element selectors (only if forceTranslate is true).
        if (this.forceTranslate) {
            this.translationSelectors.elementSelectors.forEach(({ selector }) => {
                elements.push(...root.querySelectorAll(selector));
            });
        }

        // 3. Process custom selectors.
        this.translationSelectors.customSelectors.forEach(({ selector, getKey, update }) => {
            const customElements = root.querySelectorAll(selector);
            customElements.forEach(element => {
                const key = getKey(element);
                const translatedText = this.t(key);
                update(element, translatedText);
            });
        });

        // 4. Iterate over all found elements and update them.
        elements.forEach(element => {
            let key = null;
            let originAttr = null;
            let matchingElementSelector = null;

            // • If the element already has a static data-i18n attribute,
            //   use its value as the translation key and record "data-i18n" as the origin.
            if (element.hasAttribute("data-i18n")) {
                key = element.getAttribute("data-i18n");
                if (!element.hasAttribute("data-translation-key-attr")) {
                    element.setAttribute("data-translation-key-attr", "data-i18n");
                }
                originAttr = element.getAttribute("data-translation-key-attr");
                // Also, if the element’s inner text is empty, mark it so textContent won’t be updated.
                if (!element.textContent.trim()) {
                    element.setAttribute("data-skip-text", "true");
                }
            }
            // • Otherwise, if the element matches one of our attribute selectors,
            //   use the original attribute value as the translation key.
            else {
                const matchingAttributeSelector = this.translationSelectors.attributeSelectors.find(s => element.matches(s.selector));
                if (matchingAttributeSelector) {
                    if (!element.hasAttribute("data-i18n")) {
                        const originalKey = element.getAttribute(matchingAttributeSelector.keyAttribute);
                        element.setAttribute("data-i18n", originalKey);
                        element.setAttribute("data-translation-key-attr", matchingAttributeSelector.keyAttribute);
                        if (!element.textContent.trim()) {
                            element.setAttribute("data-skip-text", "true");
                        }
                    }
                    key = element.getAttribute("data-i18n");
                    originAttr = element.getAttribute("data-translation-key-attr");
                }
            }

            // • If no key has been determined and forceTranslate applies via element selectors,
            //   use the element’s textContent.
            if (!key && this.forceTranslate) {
                matchingElementSelector = this.translationSelectors.elementSelectors.find(s => element.matches(s.selector));
                if (matchingElementSelector) {
                    key = element.textContent.trim();
                    if (!element.hasAttribute("data-i18n")) {
                        element.setAttribute("data-i18n", key);
                    }
                }
            }

            if (key) {
                const translatedText = this.t(key);

                if (originAttr) {
                    switch (originAttr) {
                        case "placeholder":
                            element.setAttribute("placeholder", translatedText);
                            break;
                        case "title":
                            // Always update the title attribute so that even icon–only elements get updated.
                            element.setAttribute("title", translatedText);
                            if (!element.hasAttribute("data-skip-text")) {
                                element.textContent = translatedText;
                            }
                            break;
                        case "data-i18n":
                            // For elements that provided a key via a static data-i18n attribute, update only textContent.
                            if (!element.hasAttribute("data-skip-text")) {
                                element.textContent = translatedText;
                            }
                            break;
                        default:
                            if (!element.hasAttribute("data-i18n")) {
                                element.setAttribute(originAttr, translatedText);
                            }
                            break;
                    }
                } else if (matchingElementSelector) {
                    // For forceTranslate and element selectors.
                    element.textContent = translatedText;
                }
            }
        });
    }





    loadUserLanguage() {
        let storedLanguage = localStorage.getItem(this.localStorageKey);

        // Validate stored language
        if (storedLanguage && !this.availableLanguages.some(lang => lang.code === storedLanguage)) {
            this.log.warn(`Stored language "${storedLanguage}" is invalid.  Trying user's default or system default.`);
            storedLanguage = null; // Invalidate if not in availableLanguages
        }

        // 1. Use stored language (if valid)
        if (storedLanguage) {
            this.currentLanguage = storedLanguage;
        } else {
            // 2. Use user's preferred language (from browser) if supported
            const userPreferredLanguage = navigator.language || navigator.userLanguage; // Get browser's language
            const supportedUserLanguage = this.availableLanguages.find(lang => lang.code === userPreferredLanguage || userPreferredLanguage.startsWith(lang.code));

            if (supportedUserLanguage) {
                this.currentLanguage = supportedUserLanguage.code;
            } else {
                // 3. Fallback to default language
                this.currentLanguage = this.defaultLanguage;
            }
        }


        // Set the language and direction (important for initial load)
        const languageInfo = this.availableLanguages.find(lang => lang.code === this.currentLanguage);
        if (languageInfo) { // Always check, even for the default language
            document.documentElement.setAttribute('lang', this.currentLanguage);
            document.documentElement.setAttribute('dir', languageInfo.dir);
        }

    }

    t(key, replacements = null) {
        if (!key) return '';

        this.untranslatedKeys.add(key);

        const translationObject = this.translations[key];
        let translatedString = key; // Default to the key itself

        if (translationObject) {
            translatedString = translationObject[this.currentLanguage] ||
                translationObject[this.defaultLanguage] ||
                key;
        }

        // Perform replacements if a replacements object is provided
        if (replacements && typeof replacements === 'object' && translatedString !== key) {
            // Simple regex to find {{variable}} style placeholders
            translatedString = translatedString.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, variableName) => {
                // If the variable exists in replacements, use its value, otherwise keep the placeholder
                return replacements.hasOwnProperty(variableName) ? replacements[variableName] : match;
            });
        } else if (replacements && translatedString === key) {
            // If the key itself wasn't found in translations but replacements are provided,
            // we might still want to apply replacements to the key if it contains placeholders.
            // This is less common but could be useful for dynamic keys that are also templates.
            translatedString = key.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, variableName) => {
                return replacements.hasOwnProperty(variableName) ? replacements[variableName] : match;
            });
        }

        return translatedString;
    }


    async generateTranslationFile() {
        const translationObject = {};
        this.untranslatedKeys.forEach(key => {
            translationObject[key] = {};
            this.availableLanguages.forEach(lang => {
                translationObject[key][lang.code] = this.translations[key]?.[lang.code] || ""; // Existing or empty
            });
        });

        return translationObject;
    }

    async saveTranslations() {
        try {
            const data = await this.generateTranslationFile();
            await this.appClient.rpcClient.call('TranslationManagement.saveTranslations', { translations: data });
            this.log.info('Translations saved successfully.');
        }
        catch (error) {
            this.log.error("Error saving translations", error);
        }
    }
}
